import { Badge, Box, Button, Card, CardBody, Flex, Heading, Spinner, Table, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ParticipationRequestStatus, useListMyParticipationRequestsQuery } from "../../../autogen/bff-api";
import { useDisplayDate } from "../../../common/formatting/useDisplayDate";
import { participationStatusBadge } from "../../../common/qualification/participationStatusBadge";
import { usePortalEventUrls } from "../../sourcing-events/portal/usePortalEventUrls";

export const SupplierRequestsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayDate = useDisplayDate();
  const portalUrls = usePortalEventUrls();

  const { data, isLoading } = useListMyParticipationRequestsQuery({});
  const rows = data?.rows ?? [];

  return (
    <>
      <Heading as="h1" size="lg">
        {t("My requests")}
      </Heading>
      <Text color="smMuted" mt="2" mb="6">
        {t("Your requests to participate in two-stage competitions")}
      </Text>

      {isLoading && rows.length === 0 ? (
        <Flex justify="center" py="20">
          <Spinner color="smPrimary" />
        </Flex>
      ) : rows.length === 0 ? (
        <Card variant="outline" rounded="xl">
          <CardBody py="20">
            <Flex align="center" justify="center">
              <Text color="smMuted">{t("You have no requests to participate yet")}</Text>
            </Flex>
          </CardBody>
        </Card>
      ) : (
        <Box bg="smBackground" border="1px solid" borderColor="smBorder" rounded="xl" overflowX="auto">
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>{t("Event")}</Th>
                <Th>{t("Buyer")}</Th>
                <Th>{t("Deadline for requests to participate")}</Th>
                <Th>{t("Status")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map(({ request, eventId, eventTitle, buyerOrganizationName, requestDeadline }) => {
                // Dynamic key for langen: t('Not selected')
                const notSelected = request.selectionOutcome != null && !request.selectionOutcome.selected;
                const badge = notSelected
                  ? { colorScheme: "orange", labelKey: "Not selected" }
                  : participationStatusBadge(request.status, "Submitted");
                const target =
                  request.status === ParticipationRequestStatus.Draft
                    ? portalUrls.request.edit.go(eventId, request.id).intro
                    : portalUrls.view.go(eventId);
                return (
                  <Tr key={request.id}>
                    <Td width="80" maxWidth="80" overflow="hidden" textAlign="left">
                      <Button variant="link" colorScheme="teal" textAlign="left" whiteSpace="pre-wrap" onClick={() => navigate(target)}>
                        {eventTitle ?? t("Unknown")}
                      </Button>
                    </Td>
                    <Td>
                      <Text>{buyerOrganizationName}</Text>
                    </Td>
                    <Td>
                      <Text>{requestDeadline ? displayDate(requestDeadline) : "–"}</Text>
                    </Td>
                    <Td>
                      <Badge colorScheme={badge.colorScheme}>{t(badge.labelKey)}</Badge>
                      {notSelected && request.selectionOutcome?.reason && (
                        <Text fontSize="xs" color="smMuted" mt="1" maxW="64" noOfLines={2}>
                          {request.selectionOutcome.reason}
                        </Text>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}
    </>
  );
};
